import { useEffect, useMemo, useRef, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { User, Truck, MagnifyingGlass, X, CaretDown, Check, Clock, AirplaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { haptic } from "../lib/haptics";
import { fmtDate } from "../lib/dateUtil";

/** Phase 236 — Inline per-task assignee picker.
 *
 * Renders a compact "Assign" trigger that opens a popover with two tabs:
 *   • In-house  — dropdown / search of active in-house operatives
 *   • Supplier  — dropdown / search of active suppliers (vendors)
 *
 * Persists by calling `PUT /api/tasks/{id}/assign` with the correct
 * `assigned_type`. Re-fetches lists once per mount and caches the result
 * in module-scope so opening multiple pickers on the same page is fast.
 *
 * Props
 *   task               — the full task object (we read assigned_to, assigned_to_name, assigned_type)
 *   onAssigned(updated)— optional callback fired after a successful assign
 *   compact            — boolean, render a tiny chip (used inside table rows)
 *   className          — extra classes for the trigger
 */

let _operativesCache = null;
let _suppliersCache = null;

const RECENTS_KEY = "fixli_task_assign_recents";
const RECENTS_MAX = 5;

function readRecents() {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function pushRecent(entry) {
  if (!entry || !entry.id) return;
  try {
    const arr = readRecents().filter(e => e.id !== entry.id);
    arr.unshift({
      id: entry.id,
      name: entry.name || "",
      type: entry.type, // "in_house" | "vendor"
      trade_or_role: entry.trade_or_role || "",
      ts: Date.now(),
    });
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(arr.slice(0, RECENTS_MAX)));
  } catch { /* ignore */ }
}
export function clearTaskAssignRecents() {
  try { window.localStorage.removeItem(RECENTS_KEY); } catch { /* ignore */ }
}

async function _fetchOperatives() {
  if (_operativesCache) return _operativesCache;
  try {
    // Phase 241 — strict in-house filter: only field operatives (team_member /
    // in_house roles), NEVER admins or PMs. Tenant-scoped server-side.
    const { data } = await API.get("/users", { params: { role: "in_house" } });
    const arr = (Array.isArray(data) ? data : data?.users) || [];
    _operativesCache = arr
      .filter(u => u.active !== false)
      .filter(u => ["team_member", "in_house"].includes(u.role))
      .map(u => ({ id: u._id || u.id, name: u.name, email: u.email, role: u.role }));
    return _operativesCache;
  } catch { return []; }
}
async function _fetchSuppliers() {
  if (_suppliersCache) return _suppliersCache;
  try {
    const { data } = await API.get("/suppliers");
    const arr = (Array.isArray(data) ? data : data?.vendors) || [];
    _suppliersCache = arr
      .filter(v => v.active !== false)
      .map(v => ({ id: v.user_id || v._id || v.id, name: v.name || v.company_name, email: v.email, trade: v.trade, company: v.company_name }));
    return _suppliersCache;
  } catch { return []; }
}

export function clearAssigneeCache() {
  _operativesCache = null;
  _suppliersCache = null;
}

export default function TaskAssigneePicker({ task, onAssigned, compact = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(task?.assigned_type === "vendor" ? "vendor" : "in_house");
  const [operatives, setOperatives] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [recents, setRecents] = useState([]);
  const [unavailMap, setUnavailMap] = useState({});
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setRecents(readRecents());
    Promise.all([_fetchOperatives(), _fetchSuppliers()]).then(([ops, vs]) => {
      setOperatives(ops); setSuppliers(vs);
      // Phase 240 — pre-fetch unavailability for everyone on the task date
      if (task?.start_date) {
        const all = [...(ops || []), ...(vs || [])];
        const map = {};
        Promise.all(all.map(async (p) => {
          try {
            const { data } = await API.get("/team/unavailability/check", { params: { user_id: p.id, date: task.start_date } });
            if (data?.unavailable) map[p.id] = data;
          } catch { /* ignore */ }
        })).then(() => setUnavailMap(map));
      }
    });
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [open, task?.start_date]);

  const list = tab === "vendor" ? suppliers : operatives;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(x =>
      (x.name || "").toLowerCase().includes(q) ||
      (x.email || "").toLowerCase().includes(q) ||
      (x.trade || "").toLowerCase().includes(q),
    );
  }, [list, search]);

  const handlePick = async (assignee) => {
    setSaving(true);
    try {
      // Phase 240 — unavailability warning when task has a start date
      if (task?.start_date) {
        try {
          const { data: chk } = await API.get("/team/unavailability/check", {
            params: { user_id: assignee.id, date: task.start_date },
          });
          if (chk?.unavailable) {
            const reason = chk.reason ? ` (${chk.reason})` : "";
            const ok = window.confirm(
              `${assignee.name || "This user"} is marked unavailable on ${fmtDate(task.start_date)}${reason}.\n\nAssign anyway?`,
            );
            if (!ok) { setSaving(false); return; }
          }
        } catch { /* ignore — non-blocking */ }
      }
      const payload = {
        assigned_to: assignee.id,
        assigned_to_name: assignee.name || assignee.email || "",
        assigned_type: tab === "vendor" ? "vendor" : "in_house",
      };
      if (tab === "vendor") {
        payload.vendor_id = assignee.id;
        payload.vendor_name = assignee.name || assignee.company || "";
      }
      await API.put(`/tasks/${task.id}/assign`, payload);
      pushRecent({
        id: assignee.id,
        name: assignee.name || assignee.email || "",
        type: tab === "vendor" ? "vendor" : "in_house",
        trade_or_role: tab === "vendor" ? (assignee.trade || "") : (assignee.role || ""),
      });
      setRecents(readRecents());
      haptic("success");
      toast.success(`Assigned to ${assignee.name || "user"}`);
      onAssigned?.({ ...task, ...payload, status: "assigned" });
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign");
    } finally { setSaving(false); }
  };

  const clearAssignee = async () => {
    setSaving(true);
    try {
      await API.put(`/tasks/${task.id}/assign`, {
        assigned_to: "", assigned_to_name: "", assigned_type: "",
      });
      haptic("medium");
      toast.success("Assignee cleared");
      onAssigned?.({ ...task, assigned_to: "", assigned_to_name: "", assigned_type: "", vendor_id: "", vendor_name: "" });
      setOpen(false);
    } catch { toast.error("Failed to clear"); }
    finally { setSaving(false); }
  };

  const pickRecent = async (r) => {
    // Resolve against the live caches so the saved name/trade is fresh
    const list = r.type === "vendor" ? suppliers : operatives;
    let entry = list.find(p => p.id === r.id);
    if (!entry) {
      // Cache might be stale or this user isn't in the current tenant — fall back to the saved snapshot
      entry = { id: r.id, name: r.name, trade: r.type === "vendor" ? r.trade_or_role : "", role: r.type === "in_house" ? r.trade_or_role : "" };
    }
    setTab(r.type === "vendor" ? "vendor" : "in_house");
    await handlePick(entry);
  };

  const currentLabel = task?.assigned_to_name || (task?.assigned_type === "vendor" ? task?.vendor_name : "");
  const currentType = task?.assigned_type;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`task-assign-trigger-${task?.id || ""}`}
          className={`inline-flex items-center gap-1.5 ${compact ? "text-[11px] px-2 py-1" : "text-xs px-2.5 py-1.5"}
            border border-dashed rounded-md hover:border-primary/60 hover:bg-muted/40 transition
            ${currentLabel ? "border-solid border-border" : "border-border text-muted-foreground"} ${className}`}
        >
          {currentType === "vendor" ? <Truck size={compact ? 11 : 13} /> : <User size={compact ? 11 : 13} />}
          <span className="truncate max-w-[120px]">{currentLabel || "Assign"}</span>
          <CaretDown size={10} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72 max-w-[calc(100vw-2rem)]" data-testid="task-assign-popover">
        {/* Tabs */}
        <div className="grid grid-cols-2 border-b border-border">
          <button
            type="button"
            onClick={() => { setTab("in_house"); haptic("light"); }}
            className={`flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition ${tab === "in_house" ? "bg-primary/10 text-primary border-b-2 border-primary" : "text-muted-foreground hover:bg-muted"}`}
            data-testid="assign-tab-in-house"
          >
            <User size={13} /> In-house
          </button>
          <button
            type="button"
            onClick={() => { setTab("vendor"); haptic("light"); }}
            className={`flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition ${tab === "vendor" ? "bg-primary/10 text-primary border-b-2 border-primary" : "text-muted-foreground hover:bg-muted"}`}
            data-testid="assign-tab-vendor"
          >
            <Truck size={13} /> Supplier
          </button>        </div>

        {/* Search */}
        <div className="p-2 border-b border-border">
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === "vendor" ? "Search supplier or trade…" : "Search in-house operative…"}
              className="h-8 pl-7 text-xs"
              data-testid="assign-search-input"
            />
          </div>
        </div>

        {/* Recents */}
        {recents.length > 0 && !search && (
          <div className="p-2 border-b border-border bg-muted/30" data-testid="assign-recents-row">
            <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5 flex items-center gap-1.5">
              <Clock size={10} /> Last used
            </p>
            <div className="flex flex-wrap gap-1">
              {recents.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  disabled={saving}
                  onClick={() => pickRecent(r)}
                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-1 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-primary/5 transition active:scale-95"
                  data-testid={`assign-recent-${r.id}`}
                  title={r.name}
                >
                  {r.type === "vendor" ? <Truck size={10} /> : <User size={10} />}
                  <span className="truncate max-w-[100px]">{r.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* List */}
        <div className="max-h-64 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">No matches</div>
          ) : (
            filtered.map((p) => {
              const isCurrent = p.id === task?.assigned_to;
              const ua = unavailMap[p.id];
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={saving}
                  onClick={() => handlePick(p)}
                  className={`w-full text-left px-3 py-2 hover:bg-muted/60 flex items-center justify-between gap-2 transition ${isCurrent ? "bg-primary/5" : ""} ${ua ? "opacity-80" : ""}`}
                  data-testid={`assign-pick-${p.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs font-medium truncate">{p.name || "(no name)"}</p>
                      {ua && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded-full font-semibold uppercase" title={`Unavailable ${ua.start_date}${ua.end_date && ua.end_date !== ua.start_date ? ` → ${ua.end_date}` : ""}${ua.reason ? ` (${ua.reason})` : ""}`}>
                          <AirplaneTilt size={9} /> {ua.reason || "off"}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {tab === "vendor" ? (p.trade || p.email || "") : (p.role?.replace(/_/g, " ") || p.email || "")}
                    </p>
                  </div>
                  {isCurrent && <Check size={14} className="text-primary shrink-0" />}
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        {currentLabel && (
          <div className="p-2 border-t border-border">
            <Button variant="ghost" size="sm" className="w-full text-xs text-red-600 hover:text-red-700" onClick={clearAssignee} disabled={saving} data-testid="assign-clear">
              <X size={12} className="mr-1" /> Clear assignee
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
